import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Trash, FloppyDisk, Eye, CloudArrowUp, ClockCounterClockwise,
  ArrowClockwise, CaretUp, CaretDown, StackSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import { BLOCKS, getBlock, makeBlock } from "@/lib/cmsBlocks";
import DOMPurify from "dompurify";

/* ---------------- Preview renderers ---------------- */
function StandardPreview({ content }) {
  // Admin-authored WYSIWYG HTML is stored verbatim on the page draft. Sanitise
  // it here (defence-in-depth) so a compromised admin, a poisoned draft, or a
  // future editor-import path can never inject <script>/on* handlers into the
  // preview or the published page. DOMPurify's default allowlist is
  // rich-text-safe — keeps <b>, <a>, <img>, tables, headings, etc.
  const html = DOMPurify.sanitize(
    content?.html || "<p class='text-muted-foreground italic'>Empty page.</p>",
    { USE_PROFILES: { html: true } },
  );
  return (
    <div className="prose prose-sm max-w-none dark:prose-invert border border-border rounded-md p-6 bg-background"
      dangerouslySetInnerHTML={{ __html: html }} />
  );
}
function LandingPreview({ content }) {
  const blocks = content?.blocks || [];
  if (!blocks.length) return <div className="p-6 border border-dashed border-border rounded-md text-sm text-muted-foreground italic">No blocks yet.</div>;
  return (
    <div className="space-y-4">
      {blocks.map((b) => {
        const B = getBlock(b.type);
        if (!B) return <div key={b.id} className="text-xs text-destructive">Unknown block: {b.type}</div>;
        const R = B.Renderer;
        return <R key={b.id} props={b.props || {}} />;
      })}
    </div>
  );
}
function PreviewByKind({ kind, content }) {
  return kind === "landing" ? <LandingPreview content={content} /> : <StandardPreview content={content} />;
}

/* ---------------- Block editor (landing) ---------------- */
function BlockEditor({ blocks, onChange }) {
  const [openId, setOpenId] = useState(null);
  const addBlock = (type) => {
    const nb = makeBlock(type);
    onChange([...(blocks || []), nb]);
    setOpenId(nb.id);
  };
  const updateBlock = (id, patch) =>
    onChange(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const removeBlock = (id) => {
    if (!window.confirm("Remove this block?")) return;
    onChange(blocks.filter((b) => b.id !== id));
  };
  const move = (id, delta) => {
    const idx = blocks.findIndex((b) => b.id === id);
    const target = idx + delta;
    if (idx < 0 || target < 0 || target >= blocks.length) return;
    const next = blocks.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-4" data-testid="block-editor">
      <div className="flex flex-wrap gap-2 p-3 border border-dashed border-border rounded-md bg-secondary/20" data-testid="block-palette">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground w-full mb-1">Add block</span>
        {Object.entries(BLOCKS).map(([type, b]) => (
          <Button key={type} size="sm" variant="outline" onClick={() => addBlock(type)}
            data-testid={`add-block-${type}`}>
            <Plus size={12} className="mr-1" />{b.label}
          </Button>
        ))}
      </div>
      <div className="space-y-3">
        {(blocks || []).map((b, idx) => {
          const B = getBlock(b.type);
          if (!B) return null;
          const Editor = B.Editor;
          const isOpen = openId === b.id;
          return (
            <Card key={b.id} className="p-0 overflow-hidden" data-testid={`block-${b.type}-${idx}`}>
              <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-secondary/30">
                <StackSimple size={13} className="text-muted-foreground" />
                <div className="text-xs font-medium">{B.label}</div>
                <div className="text-[10px] text-muted-foreground font-mono">{b.type}</div>
                <div className="flex-1" />
                <button onClick={() => move(b.id, -1)} disabled={idx === 0}
                  className="p-1 rounded hover:bg-secondary disabled:opacity-30" title="Move up"
                  data-testid={`block-up-${idx}`}><CaretUp size={12} /></button>
                <button onClick={() => move(b.id, 1)} disabled={idx === blocks.length - 1}
                  className="p-1 rounded hover:bg-secondary disabled:opacity-30" title="Move down"
                  data-testid={`block-down-${idx}`}><CaretDown size={12} /></button>
                <button onClick={() => setOpenId(isOpen ? null : b.id)}
                  className="text-[11px] px-2 py-0.5 rounded border border-border hover:bg-secondary"
                  data-testid={`block-toggle-${idx}`}>{isOpen ? "Collapse" : "Edit"}</button>
                <button onClick={() => removeBlock(b.id)}
                  className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  title="Delete" data-testid={`block-delete-${idx}`}><Trash size={12} /></button>
              </div>
              {isOpen && (
                <div className="p-4">
                  <Editor props={b.props || {}} onChange={(props) => updateBlock(b.id, { props })} />
                </div>
              )}
              {!isOpen && (
                <div className="px-4 py-3 text-xs text-muted-foreground line-clamp-2">
                  {b.props?.heading || b.props?.eyebrow || b.props?.html || <span className="italic">Empty — click Edit.</span>}
                </div>
              )}
            </Card>
          );
        })}
        {(!blocks || blocks.length === 0) && (
          <div className="p-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-md">
            Start by adding a block above.
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Preview dialog ---------------- */
function PreviewDialog({ open, onOpenChange, page, content, title }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="cms-preview-dialog">
        <DialogHeader>
          <DialogTitle>Preview · {title || page?.title}</DialogTitle>
        </DialogHeader>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground -mt-2">This is a preview of the current draft. It is NOT live.</div>
        <div className="pt-4">
          <PreviewByKind kind={page?.kind} content={content} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- History dialog ---------------- */
function HistoryDialog({ open, onOpenChange, page, onChanged }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewingVersion, setPreviewingVersion] = useState(null);

  const load = useCallback(async () => {
    if (!page) return;
    setLoading(true);
    try {
      const r = await api.get(`/platform/cms/pages/${page.id}/versions`);
      setVersions(r.data);
    } catch { toast.error("Failed to load history"); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const preview = async (v) => {
    try {
      const r = await api.get(`/platform/cms/pages/${page.id}/versions/${v.id}`);
      setPreviewingVersion(r.data);
    } catch { toast.error("Failed to load version"); }
  };
  const rollback = async (v) => {
    if (!window.confirm(`Rollback to v${v.version_number}? A new published version will be created with this content.`)) return;
    try {
      await api.post(`/platform/cms/pages/${page.id}/rollback/${v.id}`);
      toast.success(`Rolled back to v${v.version_number}`);
      onChanged();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Rollback failed"); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="cms-history-dialog">
          <DialogHeader><DialogTitle>Version history · {page?.title}</DialogTitle></DialogHeader>
          <div className="text-xs text-muted-foreground">Every publish and rollback creates an immutable version. Rollback re-publishes an older version's content as a fresh entry.</div>
          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : (
            <div className="space-y-2 mt-3">
              {versions.map((v) => {
                const isLive = v.id === page?.published_version_id;
                return (
                  <div key={v.id} className="p-3 border border-border rounded-md flex items-center gap-3"
                    data-testid={`version-row-${v.version_number}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium flex items-center gap-2">
                        v{v.version_number}
                        {isLive && <span className="text-[10px] uppercase text-emerald-700 dark:text-emerald-300 border border-emerald-500/40 px-1.5 rounded">Live</span>}
                        {v.rolled_back_from && <span className="text-[10px] uppercase text-amber-700 dark:text-amber-300 border border-amber-500/40 px-1.5 rounded">rollback of v{v.rolled_back_from}</span>}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {(v.published_at || "").slice(0, 16).replace("T", " ")}
                        {v.published_by_email && <> · by <span className="font-mono">{v.published_by_email}</span></>}
                      </div>
                      {v.change_note && <div className="text-xs text-muted-foreground mt-1 italic">"{v.change_note}"</div>}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => preview(v)}
                      data-testid={`version-preview-${v.version_number}`}>
                      <Eye size={12} className="mr-1" />Preview
                    </Button>
                    {!isLive && (
                      <Button size="sm" onClick={() => rollback(v)}
                        data-testid={`version-rollback-${v.version_number}`}>
                        <ArrowClockwise size={12} className="mr-1" />Rollback
                      </Button>
                    )}
                  </div>
                );
              })}
              {!versions.length && (
                <div className="py-6 text-center text-sm text-muted-foreground">No versions yet — publish once to seed history.</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {previewingVersion && (
        <PreviewDialog
          open={!!previewingVersion}
          onOpenChange={() => setPreviewingVersion(null)}
          page={page}
          content={previewingVersion.content}
          title={`${page?.title} · v${previewingVersion.version_number}`}
        />
      )}
    </>
  );
}

/* ---------------- New page dialog ---------------- */
function NewPageDialog({ open, onOpenChange, onCreated }) {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [kind, setKind] = useState("standard");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTitle(""); setSlug(""); setKind("standard"); } }, [open]);

  const auto = (t) => setSlug(t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));

  const submit = async () => {
    if (!title.trim() || !slug.trim()) return toast.error("Title and slug required");
    setBusy(true);
    try {
      const r = await api.post("/platform/cms/pages", { title, slug, kind });
      toast.success("Page created (draft)");
      onCreated(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="new-page-dialog">
        <DialogHeader><DialogTitle>New page</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">TITLE</Label>
            <Input value={title} onChange={(e) => { setTitle(e.target.value); if (!slug) auto(e.target.value); }} data-testid="new-page-title" />
          </div>
          <div>
            <Label className="text-xs">SLUG</Label>
            <Input className="font-mono" value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]+/g, "-"))}
              data-testid="new-page-slug" />
            <p className="text-[10px] text-muted-foreground mt-1">Public URL: <code>/cms/public/{slug || "…"}</code></p>
          </div>
          <div>
            <Label className="text-xs">TYPE</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="new-page-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard (rich text)</SelectItem>
                <SelectItem value="landing">Landing (block-based)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="new-page-submit">
            {busy ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Main page ---------------- */
export default function CMSBuilder() {
  const [pages, setPages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [page, setPage] = useState(null);         // full page detail
  const [draft, setDraft] = useState(null);       // working copy of draft_content
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [publishNote, setPublishNote] = useState("");
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [meta, setMeta] = useState({ title: "", seo_description: "" });

  const reloadPages = useCallback(async () => {
    try {
      const r = await api.get("/platform/cms/pages");
      setPages(r.data);
      if (!selectedId && r.data.length) setSelectedId(r.data[0].id);
    } catch { toast.error("Failed to load pages"); }
    finally { setLoading(false); }
  }, [selectedId]);

  useEffect(() => { reloadPages(); }, [reloadPages]);

  const loadPage = useCallback(async (id) => {
    try {
      const r = await api.get(`/platform/cms/pages/${id}`);
      setPage(r.data);
      setDraft(r.data.draft_content || (r.data.kind === "landing" ? { blocks: [] } : { html: "" }));
      setMeta({ title: r.data.title || "", seo_description: r.data.seo_description || "" });
    } catch { toast.error("Failed to load page"); }
  }, []);

  useEffect(() => { if (selectedId) loadPage(selectedId); }, [selectedId, loadPage]);

  const draftDirty = useMemo(() => {
    if (!page || !draft) return false;
    // Compare against saved draft_content (server state), not published version
    return JSON.stringify(draft) !== JSON.stringify(page.draft_content || {});
  }, [page, draft]);

  const saveDraft = async () => {
    if (!page) return;
    setSaving(true);
    try {
      const r = await api.put(`/platform/cms/pages/${page.id}/draft`, { content: draft });
      setPage(r.data);
      toast.success("Draft saved");
      // Refresh the sidebar dirty indicator
      reloadPages();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const saveMeta = async () => {
    if (!page) return;
    try {
      const r = await api.put(`/platform/cms/pages/${page.id}`, meta);
      setPage(p => ({ ...p, ...r.data }));
      toast.success("Page settings saved");
      reloadPages();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const openPublish = async () => {
    if (!page) return;
    if (draftDirty) {
      if (!window.confirm("You have unsaved draft changes. Save them first before publishing?")) return;
      await saveDraft();
    }
    setPublishNote(""); setPublishDialogOpen(true);
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const r = await api.post(`/platform/cms/pages/${page.id}/publish`, { change_note: publishNote });
      setPage(r.data);
      toast.success(`Published v${r.data.published_version_number}`);
      setPublishDialogOpen(false);
      reloadPages();
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
    finally { setPublishing(false); }
  };

  const discardDraft = async () => {
    if (!page) return;
    if (!window.confirm("Discard unsaved draft changes and restore the currently-published content?")) return;
    try {
      const r = await api.post(`/platform/cms/pages/${page.id}/discard-draft`);
      setPage(r.data);
      setDraft(r.data.draft_content);
      toast.success("Draft discarded");
      reloadPages();
    } catch (e) { toast.error(e.response?.data?.detail || "Discard failed"); }
  };

  const removePage = async (p) => {
    if (!window.confirm(`Delete “${p.title}” and all its versions? This cannot be undone.`)) return;
    try {
      await api.delete(`/platform/cms/pages/${p.id}`);
      toast.success("Page deleted");
      if (selectedId === p.id) { setSelectedId(null); setPage(null); setDraft(null); }
      reloadPages();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const isPublished = !!page?.published_version_id;
  const hasUnpublished = page?.draft_dirty || draftDirty;

  return (
    <Card className="p-0 overflow-hidden" data-testid="cms-builder">
      <div className="flex items-start justify-between p-6 pb-4 gap-4 border-b border-border">
        <div className="min-w-0">
          <div className="overline text-muted-foreground">Content</div>
          <h3 className="font-heading font-bold text-xl">CMS Builder</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Edit About, Contact, FAQ, Terms, Privacy and Landing pages. Nothing goes live until you press <strong>Publish</strong>.
          </p>
        </div>
        <Button size="sm" onClick={() => setNewOpen(true)} data-testid="cms-new-page">
          <Plus size={14} className="mr-1.5" />New page
        </Button>
      </div>
      {loading ? (
        <div className="p-12 text-center text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr]">
          {/* Page list */}
          <aside className="border-r border-border bg-secondary/20 p-3 space-y-1 md:min-h-[600px]" data-testid="cms-page-list">
            {pages.map((p) => (
              <button key={p.id} onClick={() => setSelectedId(p.id)}
                className={"w-full text-left rounded-md px-3 py-2 flex items-center gap-2 " +
                  (selectedId === p.id ? "bg-primary/10 border border-primary/30" : "hover:bg-secondary border border-transparent")}
                data-testid={`cms-page-${p.slug}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{p.title}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{p.slug} · {p.kind}</div>
                </div>
                {p.has_unpublished_changes && <span className="w-2 h-2 rounded-full bg-amber-500" title="Unpublished changes" data-testid={`cms-dirty-${p.slug}`} />}
                {!p.published_version_id && <span className="text-[9px] uppercase text-amber-700 dark:text-amber-300 border border-amber-500/40 px-1 rounded">draft</span>}
              </button>
            ))}
            {!pages.length && <div className="text-xs text-muted-foreground text-center py-6">No pages yet.</div>}
          </aside>

          {/* Editor */}
          <section className="p-6" data-testid="cms-editor">
            {!page ? (
              <div className="py-16 text-center text-sm text-muted-foreground">Select a page from the left to start editing.</div>
            ) : (
              <>
                {/* Header bar */}
                <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-heading text-2xl font-bold">{page.title}</h2>
                      {isPublished ? (
                        <span className="text-[10px] uppercase text-emerald-700 dark:text-emerald-300 border border-emerald-500/40 px-1.5 rounded" data-testid="cms-status-published">
                          Published v{page.published_version_number}
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase text-amber-700 dark:text-amber-300 border border-amber-500/40 px-1.5 rounded" data-testid="cms-status-draft">
                          Draft (never published)
                        </span>
                      )}
                      {hasUnpublished && (
                        <span className="text-[10px] uppercase text-amber-700 dark:text-amber-300 border border-amber-500/40 px-1.5 rounded flex items-center gap-1" data-testid="cms-status-unpublished">
                          Unpublished changes
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground font-mono mt-1">/cms/public/{page.slug} · {page.kind}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setPreviewOpen(true)} data-testid="cms-preview">
                      <Eye size={14} className="mr-1.5" />Preview draft
                    </Button>
                    <Button size="sm" variant="outline" onClick={saveDraft} disabled={saving || !draftDirty} data-testid="cms-save-draft">
                      <FloppyDisk size={14} className="mr-1.5" />{saving ? "Saving…" : "Save draft"}
                    </Button>
                    <Button size="sm" onClick={openPublish} disabled={publishing} data-testid="cms-publish">
                      <CloudArrowUp size={14} className="mr-1.5" />Publish
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setHistoryOpen(true)} data-testid="cms-history">
                      <ClockCounterClockwise size={14} className="mr-1.5" />History
                    </Button>
                    {isPublished && page.draft_dirty && (
                      <Button size="sm" variant="ghost" onClick={discardDraft} data-testid="cms-discard-draft">
                        Discard draft
                      </Button>
                    )}
                    {!page.is_system && (
                      <button onClick={() => removePage(page)} className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10" title="Delete" data-testid="cms-delete-page">
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Meta */}
                <Card className="p-4 mb-4 grid md:grid-cols-3 gap-3" data-testid="cms-meta-card">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Title</Label>
                    <Input value={meta.title} onChange={(e) => setMeta((m) => ({ ...m, title: e.target.value }))} onBlur={saveMeta} data-testid="cms-meta-title" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">SEO description</Label>
                    <Input value={meta.seo_description} onChange={(e) => setMeta((m) => ({ ...m, seo_description: e.target.value }))} onBlur={saveMeta}
                      placeholder="One-line summary shown in search engines / social previews." data-testid="cms-meta-seo" />
                  </div>
                </Card>

                {/* Body */}
                {page.kind === "standard" ? (
                  <RichTextEditor
                    value={draft?.html || ""}
                    onChange={(html) => setDraft({ ...draft, html })}
                    testid="cms-standard-editor"
                  />
                ) : (
                  <BlockEditor
                    blocks={draft?.blocks || []}
                    onChange={(blocks) => setDraft({ ...draft, blocks })}
                  />
                )}
              </>
            )}
          </section>
        </div>
      )}

      <NewPageDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(p) => { reloadPages(); setSelectedId(p.id); }} />
      {page && (
        <PreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} page={page} content={draft} />
      )}
      {page && (
        <HistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} page={page} onChanged={() => loadPage(page.id)} />
      )}
      {/* Publish confirmation */}
      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent className="max-w-md" data-testid="cms-publish-dialog">
          <DialogHeader><DialogTitle>Publish page</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-muted-foreground">A new immutable version will be created and set as the live version. You can always roll back later.</p>
            <div>
              <Label className="text-xs">CHANGE NOTE (optional)</Label>
              <Textarea rows={2} value={publishNote} onChange={(e) => setPublishNote(e.target.value)}
                placeholder="Updated pricing section." data-testid="cms-publish-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
            <Button onClick={publish} disabled={publishing} data-testid="cms-publish-confirm">
              {publishing ? "Publishing…" : "Publish now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
